import React, { useEffect, useRef, useState } from 'react';
import "./WysiwygStyles.css";

interface WysiwygEditorProps {
  initialHtml: string;
  onChange: (html: string) => void;
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({ initialHtml, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize the editor when component mounts
  useEffect(() => {
    if (editorRef.current) {
      // Set initial content
      editorRef.current.innerHTML = initialHtml || '';
      
      // Add event listener for input
      const handleInput = () => {
        if (editorRef.current) {
          onChange(editorRef.current.innerHTML);
        }
      };
      
      editorRef.current.addEventListener('input', handleInput);
      
      // Set up blur handler to ensure content is saved
      editorRef.current.addEventListener('blur', handleInput);
      
      // Mark as initialized
      setIsInitialized(true);
      
      // Clean up event listeners on unmount
      return () => {
        if (editorRef.current) {
          editorRef.current.removeEventListener('input', handleInput);
          editorRef.current.removeEventListener('blur', handleInput);
        }
      };
    }
  }, []);
  
  // THIS IS THE KEY FIX - Update editor content when initialHtml prop changes
  useEffect(() => {
    // Only update if the editor already exists and initialHtml changes
    if (editorRef.current && isInitialized) {
      console.log("Updating editor content with:", initialHtml);
      
      // Only update the content if it's different to avoid cursor jumps
      if (editorRef.current.innerHTML !== initialHtml) {
        editorRef.current.innerHTML = initialHtml || '';
      }
    }
  }, [initialHtml, isInitialized]);
  
  // Apply formatting command
  const execCommand = (command: string, value: string = '') => {
    if (editorRef.current) {
      // Focus the editor first
      editorRef.current.focus();
      
      // Execute the command
      document.execCommand(command, false, value);
    }
  };
  
  // Create link with prompt
  const createLink = () => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        const url = prompt('Enter the URL:', 'https://');
        if (url) {
          execCommand('createLink', url);
        }
      } else {
        alert('Please select text to create a link');
      }
    }
  };
  
  // Insert unordered list
  const insertList = () => {
    execCommand('insertUnorderedList');
  };
  
  return (
    <div className="wysiwyg-editor">
      <div className="editor-toolbar">
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('italic')}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('underline')}
          title="Underline"
        >
          <u>U</u>
        </button>
        
        <div className="toolbar-divider"></div>
        
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('formatBlock', '<h1>')}
          title="Heading 1"
        >
          H1
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('formatBlock', '<h2>')}
          title="Heading 2"
        >
          H2
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('formatBlock', '<h3>')}
          title="Heading 3"
        >
          H3
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={() => execCommand('formatBlock', '<p>')}
          title="Paragraph"
        >
          P
        </button>
        
        <div className="toolbar-divider"></div>
        
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={insertList}
          title="Bullet List"
        >
          • List
        </button>
        <button 
          type="button" 
          className="toolbar-button" 
          onClick={createLink}
          title="Insert Link"
        >
          Link
        </button>
      </div>
      
      <div
        ref={editorRef}
        className="editor-content"
        contentEditable={true}
        suppressContentEditableWarning={true}
      />
      
      <div className="editor-hints">
        <ul>
          <li>Select text and click formatting buttons to apply styles</li>
          <li>To add a link, select text first</li>
          <li>Press Enter to create a new paragraph</li>
        </ul>
      </div>
    </div>
  );
};

export default WysiwygEditor;